import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Spin, Result } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import {
  getInvitationByToken,
  acceptInvitationByToken,
} from '../../services/account.service';
import { InvitationDetails } from '../../interfaces/team';
import { getNameOfRole } from '../../services/account.service';
import './AcceptInvitation.less';
import { trackGTMPageView } from '../../utils/gtm';

/**
 * AcceptInvitation page handles the invitation acceptance flow.
 *
 * Flow:
 * 1. Shows invitation details (public — no auth required)
 * 2. User clicks "Log In" or "Create Account"
 * 3. Calls accept-by-token (public) to accept for existing users before redirecting to Auth0
 * 4. After Auth0 redirect, user lands at "/" (main UI)
 *
 * For new users (signup), accept-by-token returns 404 (no account yet).
 * The backend auto-accepts during subscription creation (processPendingInvitationsForUser).
 */
const AcceptInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
    user,
  } = useAuth0();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Track GTM page view
  useEffect(() => {
    trackGTMPageView('/accept-invitation', 'Accept Invitation');
  }, []);

  // Fetch invitation details (unauthenticated call)
  useEffect(() => {
    const fetchInvitation = async () => {
      setLoading(true);
      setError(null);
      setInvitation(null);

      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const details = await getInvitationByToken(token);
        setInvitation(details);
      } catch (err) {
        setError(
          (err as Error).message || 'This invitation is invalid or has expired'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  // If an authenticated user lands here with matching email, accept and redirect
  useEffect(() => {
    if (auth0Loading || loading || !isAuthenticated || !invitation || !token) {
      return;
    }

    if (user?.email?.toLowerCase() === invitation.email?.toLowerCase()) {
      // Accept then redirect — .then() ensures team membership is created
      // before the main UI fetches org/team data.
      acceptInvitationByToken(token)
        .catch(() => {
          // All errors are intentionally ignored:
          // - "already accepted": invitation was processed during a prior login/signup
          // - network/server errors: user is already authenticated, navigate to main UI
        })
        .then(() => navigate('/'));
    }
  }, [
    auth0Loading,
    loading,
    isAuthenticated,
    invitation,
    user?.email,
    token,
    navigate,
  ]);

  /**
   * Try to accept the invitation (for existing users) and redirect to Auth0.
   */
  const handleAuthRedirect = async (screenHint?: 'signup') => {
    if (!token) return;

    if (invitation?.email) {
      localStorage.setItem('signup_email', invitation.email);
    }

    // Accept the invitation before redirecting to Auth0.
    // Await to ensure the request completes before the page navigates away.
    // For existing users (login), this completes the acceptance immediately.
    // For new users (signup), this returns null (404) and the backend
    // auto-accepts during subscription creation.
    await acceptInvitationByToken(token).catch(() => {
      // All errors are intentionally ignored:
      // - 404: new user without account — backend auto-accepts during signup
      // - "already accepted": invitation was processed by a prior flow
      // - network/server errors: should not block the user from logging in
    });

    loginWithRedirect({
      appState: { returnTo: '/' },
      authorizationParams: {
        ...(screenHint && { screen_hint: screenHint }),
        login_hint: invitation?.email,
        invite_token: token || undefined,
      },
    });
  };

  // Loading state
  if (loading || auth0Loading) {
    return (
      <div id="accept-invitation-container">
        <div className="invitation-card">
          <Spin size="large" />
          <p className="loading-text">Loading invitation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div id="accept-invitation-container">
        <div className="invitation-card error-card">
          <Result
            status="error"
            title="Invitation Error"
            subTitle={error}
            extra={
              <Button type="primary" onClick={() => navigate('/')}>
                Go to Home
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  // Invitation not loaded
  if (!invitation) {
    return (
      <div id="accept-invitation-container">
        <div className="invitation-card">
          <Spin size="large" />
          <p className="loading-text">Loading invitation...</p>
        </div>
      </div>
    );
  }

  // User is authenticated but with different email
  if (
    isAuthenticated &&
    user?.email?.toLowerCase() !== invitation?.email?.toLowerCase()
  ) {
    return (
      <div id="accept-invitation-container">
        <div className="invitation-card">
          <h1>Email Mismatch</h1>
          <p className="mismatch-text">
            This invitation was sent to <strong>{invitation?.email}</strong>,
            but you are logged in as <strong>{user?.email}</strong>.
          </p>
          <p className="mismatch-instruction">
            Please log out and sign in with the correct email address.
          </p>
          <Button type="primary" onClick={() => navigate('/')}>
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  // Main invitation display
  return (
    <div id="accept-invitation-container">
      <div className="invitation-card">
        <h1>You&apos;re Invited!</h1>
        <p className="invitation-details">
          <strong>{invitation?.inviter_email}</strong> has invited you to join
          the <strong>{invitation?.organization_name}</strong> team as a{' '}
          <strong>{getNameOfRole(invitation?.role)}</strong>.
        </p>

        <div className="auth-section">
          <p className="auth-instruction">
            To accept this invitation, please sign in or create an account.
          </p>
          <div className="auth-buttons">
            <Button
              type="primary"
              size="large"
              onClick={() => handleAuthRedirect()}
            >
              Log In
            </Button>
            <Button size="large" onClick={() => handleAuthRedirect('signup')}>
              Create Account
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
